import React, { useEffect, useState } from "react";
import { db, auth } from "../../firebase";
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, Timestamp } from "firebase/firestore";
import { useAuth } from "../../AuthContext";
import ScanCalendarButton from "./ScanCalendarButton";

import { EVENT_CATEGORY_OPTIONS as CATEGORY_OPTIONS, EVENT_SUBCATEGORY_OPTIONS as SUBCATEGORY_OPTIONS, canonicalPill, pillAllowed, usePillStates } from "./pillCatalog";

// Who posted / last changed an event. The bar and its linked charity can
// both edit every event at the bar, so the list says who did what.
const WHO = { owner: "the bar", charity: "the charity", admin: "Bar Fun Finder" };

function whoLine(ev) {
  let posted = "";
  if (ev.source === "web") posted = "Gathered from the bar's website";
  else if (WHO[ev.createdByRole]) posted = `Posted by ${WHO[ev.createdByRole]}`;
  let edited = "";
  if (WHO[ev.updatedByRole]) {
    const at = ev.updatedAt && typeof ev.updatedAt.toDate === "function" ? ev.updatedAt.toDate() : null;
    edited = `Edited by ${WHO[ev.updatedByRole]}${at ? `, ${at.toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}`;
  }
  return [posted, edited].filter(Boolean).join(" · ");
}

const BOOST_URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/boostEvent";

function todayCentral() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

function prettyDate(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];


function formatTime(time24) {
  if (!time24) return "";
  const [h, m] = time24.split(":");
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m} ${period}`;
}

function dayOfWeekFromDate(dateStr) {
  if (!dateStr) return "";
  return DAYS[new Date(`${dateStr}T00:00:00`).getDay()];
}

function findConflict(newEvent, allEvents, excludeId) {
  return allEvents.find((ev) => {
    if (ev.id === excludeId) return false;
    if (ev.barId !== newEvent.barId) return false;
    if (ev.time !== newEvent.time) return false;

    if (newEvent.recurrence === "one-time" && ev.recurrence === "one-time") {
      return ev.date === newEvent.date;
    }

    const newDay = newEvent.recurrence === "weekly" ? newEvent.dayOfWeek : dayOfWeekFromDate(newEvent.date);
    const existingDay = ev.recurrence === "weekly" ? ev.dayOfWeek : dayOfWeekFromDate(ev.date);
    return Boolean(newDay) && newDay === existingDay;
  });
}

export default function EventsPage() {
  const { user, role, userBars } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Gambling");
  const [subcategories, setSubcategories] = useState([]);
  const [barId, setBarId] = useState("");
  const [recurrence, setRecurrence] = useState("one-time");
  const [dayOfWeek, setDayOfWeek] = useState("Monday");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [details, setDetails] = useState("");
  const [charityName, setCharityName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [boostingId, setBoostingId] = useState(null);
  const [boostMsg, setBoostMsg] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("boost");
    if (p === "paid") return "Payment received. Your event is boosted. It can take a minute to show as Boosted below.";
    if (p === "canceled") return "Boost canceled. You weren't charged.";
    return "";
  });

  // Feature an event at the top of Bar Fun Finder on the day it next happens.
  const boost = async (ev) => {
    setBoostMsg("");
    setBoostingId(ev.id);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(BOOST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ eventId: ev.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setBoostMsg(data.error || "Couldn't boost that event. Try again.");
      } else if (data.url) {
        window.location.href = data.url; // $10 Stripe checkout
        return;
      } else if (data.applied) {
        setBoostMsg(
          data.message ||
            `Boosted for ${prettyDate(data.date)}. ${data.freeLeft} free boost${data.freeLeft === 1 ? "" : "s"} left.`
        );
        setEvents((prev) => prev.map((e) => (e.id === ev.id ? { ...e, boostDate: data.date } : e)));
      }
    } catch (e) {
      setBoostMsg("Couldn't reach Bar Fun Finder. Check your connection and try again.");
    }
    setBoostingId(null);
  };

  const myBars = userBars.filter((bar) =>
    role === "owner" ? bar.ownerId === user.uid : bar.charityId === user.uid
  );

  const selectedBar = myBars.find((b) => b.id === barId);
  // Only the pills that fit this bar's state (no "Crawfish Boil" for a
  // Minnesota bar). Anything already picked stays visible.
  const pillStates = usePillStates();
  const subOptions = (SUBCATEGORY_OPTIONS[category] || []).filter(
    (item) => subcategories.includes(item) || pillAllowed(item, selectedBar?.state, pillStates)
  );
  const linkedCharityName = selectedBar?.gaming?.charityName || "";

  const loadEvents = async () => {
    setLoading(true);
    const barIds = myBars.map((b) => b.id);
    if (barIds.length === 0) {
      setEvents([]);
      setLoading(false);
      return;
    }
    const q = query(collection(db, "events"), where("barId", "in", barIds.slice(0, 10)));
    const snap = await getDocs(q);
    setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => {
    loadEvents();
  }, [userBars]);

  const toggleSubcategory = (item) => {
    setSubcategories((prev) =>
      prev.includes(item) ? prev.filter((s) => s !== item) : [...prev, item]
    );
  };

  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    setSubcategories([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!barId || !title || !time) return;
    if (recurrence === "one-time" && !date) return;

    const eventData = {
      barId,
      title,
      category,
      subcategories,
      recurrence,
      dayOfWeek: recurrence === "weekly" ? dayOfWeek : "",
      date: recurrence === "one-time" ? date : "",
      time,
      details,
      charityName: category === "Gambling" ? charityName : "",
    };

    const conflict = findConflict(eventData, events, editingId);
    if (conflict) {
      const when =
        conflict.recurrence === "weekly" ? `every ${conflict.dayOfWeek}` : `on ${conflict.date}`;
      const postedBy =
        conflict.createdByRole === "owner"
          ? " (posted by the bar)"
          : conflict.createdByRole === "charity"
          ? " (posted by the charity)"
          : "";
      const proceed = window.confirm(
        `Heads up — there's already an event at this bar ${when} at ${formatTime(conflict.time)}: "${conflict.title}"${postedBy}. Add this one anyway?`
      );
      if (!proceed) return;
    }

    if (editingId) {
      await updateDoc(doc(db, "events", editingId), {
        ...eventData,
        updatedBy: user.uid,
        updatedByRole: role,
        updatedAt: Timestamp.now(),
      });
    } else {
      await addDoc(collection(db, "events"), {
        ...eventData,
        createdBy: user.uid,
        createdByRole: role,
        createdAt: Timestamp.now(),
      });
    }

    setTitle("");
    setSubcategories([]);
    setDate("");
    setTime("");
    setDetails("");
    setCharityName("");
    setEditingId(null);
    loadEvents();
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setBarId(ev.barId);
    setTitle(ev.title);
    setCategory(ev.category || "Other");
    setSubcategories((ev.subcategories || []).map(canonicalPill));
    setRecurrence(ev.recurrence);
    setDayOfWeek(ev.dayOfWeek || "Monday");
    setDate(ev.date || "");
    setTime(ev.time);
    setDetails(ev.details || "");
    setCharityName(ev.charityName || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setTitle("");
    setCategory("Gambling");
    setSubcategories([]);
    setDate("");
    setTime("");
    setDetails("");
    setCharityName("");
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Delete this event? This can't be undone.")) return;
    await deleteDoc(doc(db, "events", eventId));
    loadEvents();
  };

  if (myBars.length === 0) {
    return (
      <p style={{ color: "#fff" }}>
        You need to claim a bar first before you can create events.
      </p>
    );
  }

  return (
    <div style={{ color: "#fff" }}>
      <h2 style={{ marginBottom: "10px" }}>
        {role === "owner" ? "Create Event" : "Create Gaming Event"}
      </h2>
      <p style={{ marginBottom: "20px", color: "#B8B0C8" }}>
        {role === "owner"
          ? "Live music, karaoke, trivia, dart league, and other bar events."
          : "Bingo, meat raffle, and other gaming events at your linked bars."}
      </p>
      <ScanCalendarButton myBars={myBars} user={user} role={role} onEventsSaved={loadEvents} />

      <form
        onSubmit={handleSubmit}
        style={{
          background: "#0D0B1A",
          padding: "20px",
          borderRadius: "12px",
          maxWidth: "500px",
          marginBottom: "30px",
        }}
      >
        <label style={{ display: "block", marginBottom: "8px" }}>Venue</label>
        <select
          value={barId}
          onChange={(e) => setBarId(e.target.value)}
          required
          style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", marginBottom: "16px" }}
        >
          <option value="">Select a bar</option>
          {myBars.map((bar) => (
            <option key={bar.id} value={bar.id}>{bar.name || "Unnamed Bar"}</option>
          ))}
        </select>

        <label style={{ display: "block", marginBottom: "8px" }}>Category</label>
        <select
          value={category}
          onChange={(e) => handleCategoryChange(e.target.value)}
          style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", marginBottom: "12px" }}
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <label style={{ display: "block", marginBottom: "8px" }}>
          What kind of {category.toLowerCase()}? (Check all that apply)
        </label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
          {subOptions.map((item) => {
            const checked = subcategories.includes(item);
            return (
              <button
                type="button"
                key={item}
                onClick={() => toggleSubcategory(item)}
                style={{
                  padding: "8px 14px",
                  borderRadius: "20px",
                  fontSize: "16px",
                  fontWeight: 500,
                  border: checked ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                  background: checked ? "rgba(168,85,247,0.15)" : "transparent",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                {item}
              </button>
            );
          })}
        </div>

        {category === "Gambling" && (
          <>
            <label style={{ display: "block", marginBottom: "8px" }}>
              Who's running this? (shown to customers for transparency)
            </label>
            {linkedCharityName && charityName !== linkedCharityName && (
              <button
                type="button"
                onClick={() => setCharityName(linkedCharityName)}
                style={{
                  display: "block",
                  marginBottom: "8px",
                  padding: "8px 14px",
                  borderRadius: "8px",
                  border: "1px solid rgba(168,85,247,0.5)",
                  background: "rgba(168,85,247,0.12)",
                  color: "#fff",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              >
                Use linked charity: {linkedCharityName}
              </button>
            )}
            <input
              type="text"
              value={charityName}
              onChange={(e) => setCharityName(e.target.value)}
              placeholder="e.g. American Legion Post 123 — or the bar's name if it's not run by a charity"
              style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", marginBottom: "16px" }}
            />
          </>
        )}

        <label style={{ display: "block", marginBottom: "8px" }}>Event Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={role === "owner" ? "Dart Tournament" : "Bingo, $1,000 coverall"}
          required
          style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", marginBottom: "16px" }}
        />

        <label style={{ display: "block", marginBottom: "8px" }}>Does this repeat?</label>
        <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
          <button
            type="button"
            onClick={() => setRecurrence("one-time")}
            style={{
              flex: 1,
              padding: "10px",
              borderRadius: "8px",
              border: recurrence === "one-time" ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
              background: recurrence === "one-time" ? "rgba(168,85,247,0.15)" : "transparent",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            One-time event
          </button>
          <button
            type="button"
            onClick={() => setRecurrence("weekly")}
            style={{
              flex: 1,
              padding: "10px",
              borderRadius: "8px",
              border: recurrence === "weekly" ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
              background: recurrence === "weekly" ? "rgba(168,85,247,0.15)" : "transparent",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Every week
          </button>
        </div>

        {recurrence === "weekly" ? (
          <>
            <label style={{ display: "block", marginBottom: "8px" }}>Day of the week</label>
            <select
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(e.target.value)}
              style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", marginBottom: "16px" }}
            >
              {DAYS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </>
        ) : (
          <>
            <label style={{ display: "block", marginBottom: "8px" }}>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", marginBottom: "16px" }}
            />
          </>
        )}

        <label style={{ display: "block", marginBottom: "8px" }}>Time</label>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          required
          style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", marginBottom: "16px" }}
        />

        <label style={{ display: "block", marginBottom: "8px" }}>Details</label>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Any extra details customers should see"
          style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", marginBottom: "16px", height: "80px" }}
        />

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            type="submit"
            style={{
              background: "linear-gradient(90deg, #6D28D9, #A855F7)",
              border: "none",
              padding: "12px",
              borderRadius: "8px",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              flex: 1,
            }}
          >
            {editingId ? "Save Changes" : "Publish Event"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.2)",
                padding: "12px 20px",
                borderRadius: "8px",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <h3 style={{ marginBottom: "6px" }}>Events at your bar</h3>
      <p style={{ margin: "0 0 8px", color: "#B8B0C8", fontSize: "15px" }}>
        {role === "owner"
          ? "Your linked charity can edit these too, so either of you can fix an event when the other is busy."
          : "The bar can edit these too, so either of you can fix an event when the other is busy."}
      </p>
      <p style={{ margin: "0 0 12px", color: "#B8B0C8", fontSize: "15px" }}>
        <strong style={{ color: "#fff" }}>Boost</strong> puts an event at the top of Bar Fun Finder with a Featured badge on the
        day it next happens, for everyone within 25 miles. $10 per event, and your first boost is free so you can try it. Bars on Pro also get 2 free boosts every month.
        Only 3 events per area can be boosted each day, first come, first served, so book early for big nights.
      </p>
      {boostMsg && (
        <p style={{ margin: "0 0 12px", color: boostMsg.startsWith("Couldn't") || boostMsg.includes("already") ? "#F472B6" : "#4ADE80" }}>
          {boostMsg}
        </p>
      )}
      {loading ? (
        <p>Loading events...</p>
      ) : events.length === 0 ? (
        <p>No events yet.</p>
      ) : (
        events.map((ev) => (
          <div
            key={ev.id}
            style={{
              background: "#0D0B1A",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "10px",
              padding: "15px",
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <h4 style={{ margin: 0 }}>{ev.title}</h4>
              <p style={{ margin: "4px 0 0", color: "#B8B0C8", fontSize: "16px" }}>
                {ev.subcategories && ev.subcategories.length > 0
                  ? `${ev.subcategories.join(", ")} · `
                  : ev.category
                  ? `${ev.category} · `
                  : ""}
                {ev.recurrence === "weekly"
                  ? `Every ${ev.dayOfWeek} at ${formatTime(ev.time)}`
                  : `${ev.date} at ${formatTime(ev.time)}`}
                {ev.charityName ? ` · Run by ${ev.charityName}` : ""}
              </p>
              {whoLine(ev) && (
                <p style={{ margin: "4px 0 0", color: "#ACA8BF", fontSize: "14px" }}>{whoLine(ev)}</p>
              )}
              {ev.boostDate && ev.boostDate >= todayCentral() && (
                <p style={{ margin: "6px 0 0", color: "#FFD27A", fontSize: "15px", fontWeight: 700 }}>
                  ★ Boosted for {prettyDate(ev.boostDate)}
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: "8px", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {!(ev.boostDate && ev.boostDate >= todayCentral()) && (
                <button
                  onClick={() => boost(ev)}
                  disabled={boostingId === ev.id}
                  style={{
                    background: "#FF3D81",
                    border: "none",
                    borderRadius: "8px",
                    color: "#0B0B12",
                    fontWeight: 700,
                    padding: "8px 12px",
                    cursor: "pointer",
                  }}
                >
                  {boostingId === ev.id ? "Boosting..." : "Boost"}
                </button>
              )}
              <button
                onClick={() => startEdit(ev)}
                style={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "8px",
                  color: "#fff",
                  padding: "8px 12px",
                  cursor: "pointer",
                }}
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(ev.id)}
                style={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "8px",
                  color: "#F472B6",
                  padding: "8px 12px",
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
